import React, { useEffect } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../lib/api';

/**
 * Redirects the removed legacy candidate detail routes to their People
 * replacement.
 *
 * `/admin/candidates/person/:personId` maps straight across. `/admin/candidates/:candidateId`
 * is keyed by candidacy, so the owning person has to be resolved first — the
 * People tool is person-scoped and has no candidacy-keyed URL.
 */
const LegacyCandidateDetailRedirect: React.FC = () => {
  const { personId, candidateId } = useParams<{ personId?: string; candidateId?: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (personId || !candidateId) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const response = await api.get(`/admin/candidates/${candidateId}`);
        if (cancelled) return;
        const resolvedPersonId = String(response.data?.data?.person_id ?? '').trim();
        if (resolvedPersonId) {
          navigate(`/admin-next/people/${resolvedPersonId}`, { replace: true });
          return;
        }
        toast.error('Candidate has no person record');
        navigate('/admin-next/people', { replace: true });
      } catch {
        if (cancelled) return;
        toast.error('Could not resolve candidate');
        navigate('/admin-next/people', { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [candidateId, personId, navigate]);

  if (personId) {
    return <Navigate to={`/admin-next/people/${personId}`} replace />;
  }

  return (
    <div className="flex items-center justify-center min-h-64">
      <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
    </div>
  );
};

export default LegacyCandidateDetailRedirect;
